import type { MeshChannel } from '@interopio/gateway/mesh/api';
import { isRunningInConnectDesktop, parseStartingContext } from './desktop.ts';
import type { Logger } from '@interopio/gateway/logging/api';
import type { Server } from 'node:net';
import { IOGateway } from '@interopio/gateway';
import { GatewayServer } from '@interopio/gateway-server';
import { gatewayDiscoveryPipeServer } from './pipes/discovery.ts';
import { gatewayCommandPipeServer } from './pipes/command.ts';

export function onGatewayStarted(log: Logger, env = process.env): (gateway: IOGateway.Gateway, shutdownHandler: () => Promise<void>) => void {
    return (gateway, shutdownHandler) => {
        const info = JSON.stringify({ ...gateway.info(), pid: process.pid });
        if (isRunningInConnectDesktop()) {
            const startingContext = parseStartingContext(env);
            const environment = startingContext.env ?? env.IO_CD_ENV ?? env["GLUE-ENV"] ?? 'DEMO';
            const region = startingContext.region ?? env.IO_CD_REGION ?? env["GLUE-REGION"] ?? 'INTEROP.IO';
            const user = env.USERNAME ?? env.USER;
            let discoveryServer: Server;
            const options = {
                logger: log, shutdownHandler: async () => {
                    if (discoveryServer) {
                        discoveryServer.close();
                    }
                    return await shutdownHandler();

                }, info, pipeNameProps: { environment, region, user }
            } as const;
            discoveryServer = gatewayDiscoveryPipeServer(options);
            gatewayCommandPipeServer(options);
        }
        else {
            log.info(`gateway started: ${info}`);
        }
    }
}

export async function serverGatewayConfig(gateway: {
                                              enabled?: boolean,
                                              contexts: {
                                                  lifetime?: 'ref-counted' | 'ownership' | 'retained'
                                              }
                                          },
                                          bridge: {
                                              meshChannel: MeshChannel,
                                              wsPingInterval?: number,
                                              uuid: string
                                          }, env = process.env): Promise<GatewayServer.ServerConfig['gateway']> {
    let enabled = gateway.enabled;
    if (enabled === undefined) {
        if (isRunningInConnectDesktop(env)) {
            const applicationConfig = parseStartingContext(env).applicationConfig;
            enabled = applicationConfig?.customProperties?.gatewayApp === true || applicationConfig?.name === 'io-connect-gateway';
        }
    }
    let contextsLifetime = gateway.contexts.lifetime;
    if (contextsLifetime === undefined) {
        if (isRunningInConnectDesktop(env)) {
            contextsLifetime = 'retained';
        }
    }
    if (enabled) {
        return {
            clients: {
                inactive_seconds: 0,
            },
            ping: bridge.wsPingInterval ?? undefined,
            mesh: {
                auth: { user: null },
                node: bridge.uuid,
                channel: bridge.meshChannel
            },
            contexts: {
                lifetime: contextsLifetime,
                visibility: [
                    { context: /___channel___.+/, restrictions: 'cluster' },
                    { context: /T42\..*/, restrictions: 'local' },
                    { context: "___platform_prefs___", restrictions: 'local' },
                    { context: /___workspace___.+/, restrictions: 'local' },
                    { context: /___window-hibernation___.+/, restrictions: 'local' },
                    { context: /___instance___.+/, restrictions: 'local' },
                    { context: /___window___.+/, restrictions: 'local' },
                    { restrictions: 'cluster' }
                ]
            },
            methods: {
                visibility: [
                    { method: /T42\..*/, restrictions: 'local' },
                    { restrictions: 'cluster' }
                ]
            },
            peers: {
                visibility: [
                    { domain: 'context', restrictions: 'cluster' },
                    { domain: 'interop', restrictions: 'cluster' },
                    { domain: 'bus', restrictions: 'local' }
                ]
            }
        }
    }
}
